import { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  extractErrorMessage,
  useGetRrcCompanyProfilesByCompanyQuery,
  useGetCurrentRrcCompanyProfilesByCompanyQuery,
} from '@ots-frontend/ots-data';
import { AdminPage, type AdminPageProps } from '../../components/AdminPage';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsCompanySelect } from '../../select/OtsEntitySelects';
import { OtsThemeRoot } from '../../theme/OtsTheme';
import { useApp, useAppDensity } from '../../app/AppContext';
import { useAuth } from '../../app/AuthProvider';
import { RrcCompanyProfilesView } from './RrcCompanyProfilesView';
import { RrcCompanyProfileInspector } from './RrcCompanyProfileInspector';

export type RrcCompanyProfilesPageProps = {
  companyId?: string;
  companyName?: string;
  compact?: boolean;
  embedded?: boolean;
  readOnly?: boolean;
  layout?: AdminPageProps['layout'];
};
const CompanyProfiles = ({
  companyId: fixedCompanyId,
  companyName: fixedCompanyName,
  compact: compactProp,
  embedded,
  layout = 'document',
  readOnly = false,
}: RrcCompanyProfilesPageProps) => {
  const auth = useAuth();
  const app = useApp();
  const density = useAppDensity();
  const compact = compactProp ?? density === 'compact';
  const fill = layout === 'fill';
  const [companyId, setCompanyId] = useState(fixedCompanyId);
  const [companyName, setCompanyName] = useState(fixedCompanyName);
  const [includeHistory, setIncludeHistory] = useState(false);
  const [selectedId, select] = useState<string>();
  const [creating, setCreating] = useState(false);
  const canView = auth.hasAccess('rrccompanyprofile_view');
  const canCreate = !readOnly && auth.hasAccess('rrccompanyprofile_create');
  const currentQuery = useGetCurrentRrcCompanyProfilesByCompanyQuery(
    canView && companyId && !includeHistory ? companyId : skipToken
  );
  const historyQuery = useGetRrcCompanyProfilesByCompanyQuery(
    canView && companyId && includeHistory ? companyId : skipToken
  );
  const query = includeHistory ? historyQuery : currentQuery;
  const content = !canView ? (
    <OtsInlineAlert title="Access denied" tone="warning">
      You do not have permission to view company profiles.
    </OtsInlineAlert>
  ) : (
    <>
      {!fixedCompanyId && (
        <div className={fill ? 'shrink-0 max-w-lg' : 'mb-4 max-w-lg'}>
          <OtsCompanySelect
            label="Company"
            regionId={app?.regionId}
            value={companyId}
            onChange={(value) => {
              setCompanyId(value);
              select(undefined);
              setCreating(false);
            }}
            onEntityChange={(entity) => setCompanyName(entity?.displayName)}
            size={compact ? 'compact' : 'default'}
          />
        </div>
      )}
      {!companyId ? (
        <OtsInlineAlert title="Select a company">
          Choose a company to manage its RRC registrations and profile history.
        </OtsInlineAlert>
      ) : (
        <RrcCompanyProfilesView
          fill={fill}
          profiles={(query.currentData ?? []).filter((profile) =>
            auth.hasAccess(profile, 'rrccompanyprofile_view')
          )}
          loading={query.isFetching}
          error={
            query.error
              ? extractErrorMessage(
                  query.error,
                  'Company profiles could not be loaded.'
                )
              : undefined
          }
          compact={compact}
          includeHistory={includeHistory}
          onIncludeHistory={setIncludeHistory}
          onRefresh={() => void query.refetch()}
          onOpen={(profile) => select(profile.id)}
          onNew={canCreate ? () => setCreating(true) : undefined}
        />
      )}
      {selectedId && (
        <RrcCompanyProfileInspector
          id={selectedId}
          compact={compact}
          readOnly={readOnly}
          onClose={() => select(undefined)}
          onChanged={() => void query.refetch()}
        />
      )}
      {creating && !readOnly && (
        <RrcCompanyProfileInspector
          initialMode="create"
          defaults={{ companyId, companyName }}
          companyLocked
          compact={compact}
          readOnly={readOnly}
          onClose={() => setCreating(false)}
          onChanged={() => void query.refetch()}
        />
      )}
    </>
  );
  const body = (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col gap-4' : 'contents'}>
      {content}
    </div>
  );
  return (
    <OtsThemeRoot
      className={fill ? 'flex h-full min-h-0 flex-1 flex-col' : undefined}
    >
      {embedded ? (
        body
      ) : (
        <AdminPage
          title="Company compliance profiles"
          eyebrow="Texas RRC"
          fullWidth
          layout={layout}
          density={compact ? 'compact' : 'default'}
          subtitle="Company registrations, certification settings, and effective version history."
        >
          <div
            className={
              fill ? 'mt-4 flex min-h-0 flex-1 flex-col overflow-auto' : 'mt-5'
            }
          >
            {body}
          </div>
        </AdminPage>
      )}
    </OtsThemeRoot>
  );
};
export const RrcCompanyProfilesPage = (props: RrcCompanyProfilesPageProps) => (
  <CompanyProfiles key={props.companyId ?? 'company-selector'} {...props} />
);
